import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Users, MapPin, Briefcase, Link as LinkIcon, Shield, Loader2, AlertCircle, Calendar, Building2, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { verifyLinkedInForLead } from '@/services/webSearchService';
import { useToast } from '@/hooks/use-toast';
import { LinkedInData } from '@/types/lead';
import { StatusBadge } from '@/components/websearch/StatusBadge';
import { LinkedInUrlDialog } from './LinkedInUrlDialog';
import { formatDateYMD } from '@/utils/dateFormat';

const PLACEHOLDERS = [
  'not found',
  'not available',
  'not specified',
  'not mentioned',
  'not explicitly mentioned',
  'are not explicitly mentioned',
  'not provided',
  'unknown',
  'n/a',
  'none found',
  'no information',
  'information not available',
  'information not accessible',
  'not accessible',
  'requires login',
  'login required',
  'requires sign in',
  'sign in',
  'sign-in required',
  'members only',
  'private',
  'restricted',
  'access denied',
  'publicly unavailable',
  'not publicly available',
  'no public information',
  'cannot access',
  'unable to access',
  'content unavailable'
];

const isPlaceholder = (v?: string | null) => {
  if (!v) return true;
  const val = v.toLowerCase().trim();
  return !val || PLACEHOLDERS.some(p => val === p || val.includes(p));
};

const sanitizeVal = (v?: string | null) => isPlaceholder(v) ? null : v!.trim();

interface LinkedInDataViewProps {
  data: LinkedInData;
  leadId?: string;
  onVerify?: () => void;
}

export const LinkedInDataView: React.FC<LinkedInDataViewProps> = ({ data, leadId, onVerify }) => {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Sanitize all fields to ensure no placeholders are rendered
  const hasVerifiedData = data && (data as any).dataSource === 'verified_linkedin';
  const dataVerifiedAt = data && (data as any).dataVerifiedAt ? new Date((data as any).dataVerifiedAt) : null;
  const description = data ? sanitizeVal(data.companyDescription) : null;
  const website = data ? sanitizeVal(data.website) : null;
  const rawWebsite = data ? sanitizeVal(data.rawWebsite) : null;
  const websiteSource = data?.websiteSource || 'unknown';
  const redirectDetected = data?.redirectDetected || false;
  const employeeCount = data ? sanitizeVal(data.employeeCount) : null;
  const companySize = data ? sanitizeVal((data as any).companySize) : null;
  const industry = data ? sanitizeVal(data.industry) : null;
  const headquarters = data ? sanitizeVal(data.headquarters) : null;
  const specialties = data && Array.isArray(data.specialties)
    ? data.specialties.map(sanitizeVal).filter(Boolean) as string[]
    : [];
  const foundedYear = data?.foundedYear;
  const companyPageUrl = data?.companyPageUrl;
  const companyLogoUrl = data ? sanitizeVal((data as any).companyLogoUrl) : null;

  const hasAnyData = description || website || employeeCount || companySize || industry || headquarters || specialties.length > 0 || foundedYear;

  // Determine the data state
  const hasUnverifiedData = data && (data as any).dataSource === 'web_search' && companyPageUrl;
  const dataNotFound = data && (data as any).dataSource === 'not_found';

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleVerifyLinkedIn = async (url: string) => {
    if (!leadId) return;
    
    try {
      toast({ title: "Verifying LinkedIn page...", description: "This may take a moment." });
      
      // Trigger the verification
      await verifyLinkedInForLead(leadId, url);
      
      toast({ title: "Verification started", description: "The page will refresh with updated data shortly." });
      
      // Notify parent to refresh
      if (onVerify) {
        setTimeout(onVerify, 2000);
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast({ 
        variant: "destructive",
        title: "Verification failed", 
        description: "Please check the URL and try again." 
      });
    }
  };

  return (
    <Card className="border-border/80 shadow-md hover:shadow-lg transition-all duration-300">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            {/* Company Logo */}
            {companyLogoUrl ? (
              <img 
                src={companyLogoUrl} 
                alt="Company logo"
                className="h-12 w-12 rounded-lg object-cover border border-border/50 flex-shrink-0"
                onError={(e) => {
                  // Hide image and show fallback if load fails
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling;
                  if (fallback) (fallback as HTMLElement).style.display = 'flex';
                }}
              />
            ) : null}
            
            {/* Fallback LinkedIn icon if no logo or if image fails */}
            <div 
              className="h-12 w-12 rounded-lg bg-muted/30 flex items-center justify-center flex-shrink-0"
              style={{ display: companyLogoUrl ? 'none' : 'flex' }}
            >
              <svg className="h-6 w-6 text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </div>
            
            <CardTitle className="flex items-center gap-2.5 text-xl">
              LinkedIn Information
            </CardTitle>
          </div>
          
          {hasVerifiedData && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
              <Badge variant="secondary" className="text-xs">Verified</Badge>
              {dataVerifiedAt && (
                <span>• {formatDateYMD(dataVerifiedAt)}</span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {hasVerifiedData && companyPageUrl ? (
          /* State 2: Verified data available */
          <>
            {companyPageUrl && (
              <div>
                <a 
                  href={companyPageUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:text-primary/80 hover:underline underline-offset-4 transition-colors font-medium"
                >
                  View LinkedIn Company Page
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
            
            {!hasAnyData && (
              <div className="text-sm text-muted-foreground py-4">
                Limited information available for this company.
              </div>
            )}

            {description && (
              <div>
                <h4 className="font-semibold mb-2 text-foreground">About</h4>
                <p className="text-sm text-muted-foreground/90 leading-relaxed">{description}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {website && (
                <div className="flex items-center gap-2.5 bg-muted/30 px-4 py-3 rounded-lg md:col-span-2">
                  <LinkIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <a 
                    href={website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-primary hover:underline underline-offset-2 truncate"
                  >
                    {website.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                  {/* Website source badge */}
                  <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                    {websiteSource === 'google_places' && (
                      <Badge variant="secondary" className="text-xs font-medium">
                        ✓ Google Places
                      </Badge>
                    )}
                    {websiteSource === 'linkedin_verified' && (
                      <Badge variant="secondary" className="text-xs font-medium">
                        ✓ LinkedIn
                      </Badge>
                    )}
                    {websiteSource === 'linkedin_scraped' && (
                      <Badge variant="outline" className="text-xs">
                        LinkedIn
                      </Badge>
                    )}
                    {redirectDetected && (
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        Redirect Fixed
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {employeeCount && (
                <div className="flex items-center gap-2.5 bg-muted/30 px-4 py-3 rounded-lg">
                  <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm">
                    <span className="font-semibold text-foreground">
                      {employeeCount}
                      {!/employees?/i.test(employeeCount) && <span className="text-muted-foreground"> employees</span>}
                    </span>
                  </span>
                </div>
              )}

              {companySize && (
                <div className="flex items-center gap-2.5 bg-muted/30 px-4 py-3 rounded-lg">
                  <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm">
                    <span className="font-semibold text-foreground">Company Size: </span>
                    <span className="text-muted-foreground">{companySize}</span>
                  </span>
                </div>
              )}

              {industry && (
                <div className="flex items-center gap-2.5 bg-muted/30 px-4 py-3 rounded-lg">
                  <Briefcase className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">{industry}</span>
                </div>
              )}

              {headquarters && (
                <div className="flex items-center gap-2.5 bg-muted/30 px-4 py-3 rounded-lg md:col-span-2">
                  <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">{headquarters}</span>
                </div>
              )}

              {foundedYear && (
                <div className="flex items-center gap-2.5 bg-muted/30 px-4 py-3 rounded-lg">
                  <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm">
                    <span className="font-semibold text-foreground">Founded: </span>
                    <span className="text-muted-foreground">{foundedYear}</span>
                  </span>
                </div>
              )}
            </div>

            {specialties && specialties.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 text-foreground">Specialties</h4>
                <div className="flex flex-wrap gap-2">
                  {specialties.map((specialty, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary"
                      className="px-3 py-1 font-medium"
                    >
                      {specialty}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : hasUnverifiedData && !hasVerifiedData ? (
          /* State 3: Unverified web search data found - show URL and allow edit/retry */
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
                <svg className="h-8 w-8 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                LinkedIn company page found
              </p>
            </div>
            
            {/* Show the found URL */}
            <div className="bg-muted/30 px-4 py-3 rounded-lg">
              <label className="text-xs text-muted-foreground mb-1 block">LinkedIn URL</label>
              <a 
                href={companyPageUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:underline break-all inline-flex items-center gap-1"
              >
                {companyPageUrl}
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleOpenDialog}
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                Edit URL
              </Button>
              <Button 
                className="flex-1"
                onClick={() => handleVerifyLinkedIn(companyPageUrl!)}
              >
                <Shield className="h-4 w-4 mr-2" />
                Verify & Enrich
              </Button>
            </div>
            
            <p className="text-xs text-center text-muted-foreground">
              Click "Verify & Enrich" to scrape company details from LinkedIn
            </p>
          </div>
        ) : dataNotFound ? (
          /* State 4: LinkedIn not found - manual verification available */
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
              <svg className="h-8 w-8 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              No LinkedIn company page found automatically.
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              You can manually verify the company's LinkedIn page.
            </p>
            {leadId && (
              <Button variant="outline" onClick={handleOpenDialog}>
                <Shield className="h-4 w-4 mr-2" />
                Verify LinkedIn Page
              </Button>
            )}
          </div>
        ) : (
          /* State 5: No LinkedIn data - manual verification available */
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
              <svg className="h-8 w-8 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              No LinkedIn data available
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Verify the company's LinkedIn page manually
            </p>
            {leadId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenDialog}
              >
                <Shield className="h-4 w-4 mr-2" />
                Add LinkedIn URL
              </Button>
            )}
          </div>
        )}
      </CardContent>
      
      <LinkedInUrlDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleVerifyLinkedIn}
      />
    </Card>
  );
};
